import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  validateFamilyCsv,
  reconcileFamilyImport,
  type ImportSummary,
  FAMILY_REQUIRED_HEADERS,
  type FamilyInput,
} from '@pirouette/shared';
import {
  fetchExistingFamilyEmails,
  useCommitFamilyImport,
  useCreateImportBatch,
} from '@/data/imports';
import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/Empty';
import { ImportSummaryView } from '@/components/import/ImportSummaryView';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/families/import')({
  component: FamilyImportWizard,
});

type Step = 'upload' | 'preview' | 'committing' | 'done';

interface PreviewState {
  filename: string;
  validRows: { rowNum: number; data: FamilyInput }[];
  summary: ImportSummary;
}

function FamilyImportWizard() {
  const navigate = useNavigate();
  const createBatch = useCreateImportBatch();
  const commit = useCommitFamilyImport();
  const [step, setStep] = useState<Step>('upload');
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  async function handleFile(file: File) {
    setErrorText(null);
    try {
      const text = await file.text();
      const result = validateFamilyCsv(text);

      // Bounded DB collision check — only the candidate emails are sent.
      const candidates = result.validRows.map((r) => r.data.primary_guardian_email);
      const existing = await fetchExistingFamilyEmails(candidates);
      const reconciled = reconcileFamilyImport(result, existing);

      setPreview({
        filename: file.name,
        validRows: reconciled.validRows,
        summary: reconciled.summary,
      });
      setStep('preview');
    } catch (err) {
      setErrorText(err instanceof Error ? err.message : 'Could not read the file.');
    }
  }

  async function handleCommit() {
    if (!preview) return;
    setStep('committing');
    try {
      const batch = await createBatch.mutateAsync({
        kind: 'families',
        filename: preview.filename,
        rowCount: preview.summary.totalRows,
      });
      const result = await commit.mutateAsync({
        batchId: batch.id,
        rows: preview.validRows.map((v) => v.data),
      });
      toast({
        title:
          result.failed === 0
            ? 'Import complete'
            : result.committed === 0
              ? 'Import failed'
              : 'Import partially complete',
        description:
          result.failed === 0
            ? `${result.committed} families added.`
            : `${result.committed} added, ${result.failed} failed. Check the import batch for details.`,
        variant: result.failed === 0 ? 'success' : 'error',
      });
      setStep('done');
    } catch (err) {
      setErrorText(err instanceof Error ? err.message : 'Import failed.');
      setStep('preview');
    }
  }

  return (
    <div className="max-w-4xl space-y-8">
      <header>
        <Link
          to="/admin/families"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← All families
        </Link>
        <p className="mt-2 tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Bulk import
        </p>
        <h1 className="mt-2">Import families from CSV</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Upload a CSV exported from your current studio software. We'll show you a preview before
          anything is saved.
        </p>
      </header>

      <ol aria-label="Wizard steps" className="flex gap-2 text-[var(--text-sm)]">
        {(['upload', 'preview', 'done'] as const).map((s, idx) => {
          const isActive = s === step || (step === 'committing' && s === 'preview');
          const isDone =
            (step === 'preview' && s === 'upload') || (step === 'done' && s !== 'done');
          return (
            <li
              key={s}
              aria-current={isActive ? 'step' : undefined}
              className={`flex items-center gap-2 rounded-full px-3 py-1 ${
                isActive
                  ? 'bg-[var(--color-accent-muted)] font-medium text-[var(--color-ink)]'
                  : isDone
                    ? 'text-[var(--color-success)]'
                    : 'text-[var(--color-ink-subtle)]'
              }`}
            >
              <span className="tabular-nums">{idx + 1}</span>
              <span className="capitalize">{s}</span>
            </li>
          );
        })}
      </ol>

      {step === 'upload' && (
        <section aria-labelledby="upload-heading" className="surface-elevated space-y-5 p-6">
          <h2 id="upload-heading" className="font-medium text-[var(--text-lg)]">
            Upload your CSV
          </h2>
          <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            Required columns: {FAMILY_REQUIRED_HEADERS.join(', ')}.
          </p>
          <label className="block">
            <span className="sr-only">CSV file</span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="block w-full text-[var(--text-sm)] file:mr-4 file:rounded-[var(--radius-md)] file:border-0 file:bg-[var(--color-accent)] file:px-4 file:py-2 file:font-medium file:text-[var(--color-ink-on-accent)] hover:file:bg-[var(--color-accent-hover)]"
              onChange={(e) => {
                const file = e.currentTarget.files?.[0];
                if (file) void handleFile(file);
              }}
            />
          </label>
          {errorText && (
            <p role="alert" className="text-[var(--color-danger)] text-[var(--text-sm)]">
              {errorText}
            </p>
          )}
        </section>
      )}

      {step === 'preview' && preview && (
        <section aria-labelledby="preview-heading" className="space-y-5">
          <h2 id="preview-heading" className="font-medium text-[var(--text-lg)]">
            Preview · {preview.filename}
          </h2>

          <ImportSummaryView summary={preview.summary} />

          {errorText && (
            <p role="alert" className="text-[var(--color-danger)] text-[var(--text-sm)]">
              {errorText}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <Button disabled={preview.validRows.length === 0} onClick={() => void handleCommit()}>
              Commit {preview.validRows.length} {preview.validRows.length === 1 ? 'row' : 'rows'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setPreview(null);
                setStep('upload');
              }}
            >
              Upload a different file
            </Button>
          </div>
        </section>
      )}

      {step === 'committing' && (
        <p aria-live="polite" className="text-[var(--color-ink-muted)]">
          Saving rows…
        </p>
      )}

      {step === 'done' && (
        <Empty
          title="Import complete"
          description="The new families are now in the roster. Want to add dancers to those families next?"
          action={
            <div className="flex gap-2">
              <Button onClick={() => void navigate({ to: '/admin/dancers/import' })}>
                Import dancers
              </Button>
              <Button variant="secondary" onClick={() => void navigate({ to: '/admin/families' })}>
                Back to roster
              </Button>
            </div>
          }
        />
      )}
    </div>
  );
}
